import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { LoaderCircle, Trash2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EntityCardImageField } from "@/components/entities/entity-card-image-field";
import type { EntityEditorSaveState } from "@/components/entities/entity-workspace-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createEntityDraft,
  entityCardTypeDescriptionKeys,
  entityCardTypeLabelKeys,
  entityItemCategories,
  entityItemCategoryLabelKeys,
  formatEntityErrorMessage,
  formatEntityAliasesInput,
  parseEntityAliasesInput,
  type CreateEntityCardInput,
  type EntityCardTargetOption,
  type EntityCardType,
  type EntityCardView,
  type EntityItemCategory,
  type EntityLoreOption,
} from "@/features/entities";
import { cn } from "@/lib/utils";

type EntityCardFormProps = {
  availableCharacterTargets: EntityCardTargetOption[];
  availableLoreEntries: EntityLoreOption[];
  createDraftStorageKey?: string | null;
  entity: EntityCardView | null;
  initialDraft?: Partial<Omit<CreateEntityCardInput, "projectId">> | null;
  mode: "create" | "edit";
  onCancelCreate?: () => void;
  onDelete?: () => void;
  onGenerateImage?: () => Promise<void>;
  onMediaLoadError?: () => void;
  onSave: (input: Omit<CreateEntityCardInput, "projectId">) => Promise<unknown>;
  onSaveStateChange: (
    saveState: EntityEditorSaveState,
    saveError: string | null,
  ) => void;
  type: EntityCardType;
};

const NONE_VALUE = "__none__";
const BROKEN_PREFIX = "__broken__:";
const LINE_INPUT_CLASS_NAME =
  "h-auto rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] text-ink shadow-none placeholder:text-[#b7aa9a] focus:border-clay/45 focus:ring-0";
const LINE_TEXTAREA_CLASS_NAME =
  "min-h-[7rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-8 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45";
const LINE_TEXTAREA_THIN_CLASS_NAME =
  "min-h-[3.75rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-7 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45";
const LINE_TEXTAREA_MEDIUM_CLASS_NAME =
  "min-h-[9.5rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-8 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45";
const LINE_TEXTAREA_LARGE_CLASS_NAME =
  "min-h-[18rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-8 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45 xl:min-h-[28rem]";
const LINE_SELECT_TRIGGER_CLASS_NAME =
  "h-auto rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-left text-[1.05rem] shadow-none focus:ring-0 data-[placeholder]:text-[#b7aa9a]";
const ENTITY_CREATE_DRAFT_STORAGE_KEY_PREFIX = "entities:create-draft:";

type EntityCreateDraftSnapshot = {
  aliasesText: string;
  characterDescriptionText: string;
  draft: Omit<CreateEntityCardInput, "projectId">;
};

function buildCharacterDescriptionDraft(
  draft: Pick<
    Omit<CreateEntityCardInput, "projectId">,
    "personalityTraits" | "physicalDescription"
  >,
) {
  const segments = [draft.physicalDescription, draft.personalityTraits]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);

  return segments.join("\n\n");
}

function toDraft(
  entity: EntityCardView | null,
  type: EntityCardType,
  initialDraft: Partial<Omit<CreateEntityCardInput, "projectId">> | null = null,
): Omit<CreateEntityCardInput, "projectId"> {
  if (!entity) {
    return {
      ...createEntityDraft(type),
      ...initialDraft,
    };
  }

  return {
    ageText: entity.ageText,
    aliases: entity.aliases,
    creatureAbilities: entity.creatureAbilities,
    creatureDescription: entity.creatureDescription,
    currentHolderEntityCardId: entity.currentHolderLink?.targetId ?? null,
    imageFile: null,
    itemCategory: entity.itemCategory,
    itemDescription: entity.itemDescription,
    locationDescription: entity.locationDescription,
    locationNotes: entity.locationNotes,
    machineCapabilities: entity.machineCapabilities,
    machineDescription: entity.machineDescription,
    name: entity.name,
    notes: entity.notes,
    origin: entity.origin,
    personalityTraits: entity.personalityTraits,
    physicalDescription: entity.physicalDescription,
    raceLoreEntryId: entity.raceLink?.targetId ?? null,
    removeImage: false,
    skillsAndAbilities: entity.skillsAndAbilities,
    speciesLoreEntryId: entity.speciesLink?.targetId ?? null,
    type: entity.type,
  };
}

function getEntityCreateDraftStorageKey(key: string | null | undefined) {
  return key ? `${ENTITY_CREATE_DRAFT_STORAGE_KEY_PREFIX}${key}` : null;
}

function readStoredCreateDraft(
  key: string | null | undefined,
): EntityCreateDraftSnapshot | null {
  const storageKey = getEntityCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) as EntityCreateDraftSnapshot : null;
  } catch {
    return null;
  }
}

function writeStoredCreateDraft(
  key: string | null | undefined,
  snapshot: EntityCreateDraftSnapshot,
) {
  const storageKey = getEntityCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return;
  }

  window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
}

function clearStoredCreateDraft(key: string | null | undefined) {
  const storageKey = getEntityCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return;
  }

  window.sessionStorage.removeItem(storageKey);
}

function buildCreateDraftSnapshot(
  entity: EntityCardView | null,
  type: EntityCardType,
  initialDraft: Partial<Omit<CreateEntityCardInput, "projectId">> | null,
  createDraftStorageKey: string | null | undefined,
) {
  const storedDraft = initialDraft ? null : readStoredCreateDraft(createDraftStorageKey);
  if (storedDraft) {
    return storedDraft;
  }

  const draft = toDraft(entity, type, initialDraft);
  return {
    aliasesText: formatEntityAliasesInput(draft.aliases ?? []),
    characterDescriptionText: buildCharacterDescriptionDraft(draft),
    draft,
  };
}

function toSelectValue(selectedId: string | null, availableIds: Set<string>) {
  if (!selectedId) {
    return NONE_VALUE;
  }

  return availableIds.has(selectedId)
    ? selectedId
    : `${BROKEN_PREFIX}${selectedId}`;
}

function getNamePlaceholder(type: EntityCardType, t: (key: string) => string) {
  switch (type) {
    case "character":
      return t("entities.form.placeholders.characterName");
    case "item-object":
      return t("entities.form.placeholders.itemName");
    case "creature":
      return t("entities.form.placeholders.creatureName");
    case "location":
      return t("entities.form.placeholders.locationName");
    case "machine":
      return t("entities.form.placeholders.machineName");
    default:
      return "";
  }
}

function MissingSelectionMessage({ targetId }: { targetId: string }) {
  const { t } = useTranslation();

  return (
    <p className="inline-flex items-start gap-2 text-xs text-destructive">
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {t("entities.form.missingLink", { targetId })}
    </p>
  );
}

function EditorialField({
  children,
  className,
  htmlFor,
  label,
}: {
  children: ReactNode;
  className?: string;
  htmlFor: string;
  label: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      <Label className="text-[1.05rem] font-semibold tracking-tight text-foreground" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function EditorialTextBlock({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  label: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      <p className="text-[1.05rem] font-semibold tracking-tight text-foreground">{label}</p>
      {children}
    </div>
  );
}

export function EntityCardForm({
  availableCharacterTargets,
  availableLoreEntries,
  createDraftStorageKey = null,
  entity,
  initialDraft = null,
  mode,
  onCancelCreate,
  onDelete,
  onGenerateImage,
  onMediaLoadError,
  onSave,
  onSaveStateChange,
  type,
}: EntityCardFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() =>
    buildCreateDraftSnapshot(entity, type, initialDraft, createDraftStorageKey).draft,
  );
  const [aliasesText, setAliasesText] = useState(() =>
    buildCreateDraftSnapshot(entity, type, initialDraft, createDraftStorageKey).aliasesText,
  );
  const [removeImage, setRemoveImage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stagedImage, setStagedImage] = useState<{ file: File; url: string } | null>(null);
  const [characterDescriptionText, setCharacterDescriptionText] = useState(() =>
    buildCreateDraftSnapshot(entity, type, initialDraft, createDraftStorageKey)
      .characterDescriptionText,
  );
  const entityResetRef = useRef(entity);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const saveStateChangeRef = useRef(onSaveStateChange);

  useEffect(() => {
    entityResetRef.current = entity;
  }, [entity]);

  useEffect(() => {
    saveStateChangeRef.current = onSaveStateChange;
  }, [onSaveStateChange]);

  useEffect(() => {
    return () => {
      if (stagedImage) {
        URL.revokeObjectURL(stagedImage.url);
      }
    };
  }, [stagedImage]);

  useEffect(() => {
    const currentEntity = entityResetRef.current;
    const nextSnapshot =
      mode === "create"
        ? buildCreateDraftSnapshot(currentEntity, type, initialDraft, createDraftStorageKey)
        : null;

    setDraft(nextSnapshot?.draft ?? toDraft(currentEntity, type, initialDraft));
    setCharacterDescriptionText(
      nextSnapshot?.characterDescriptionText
        ?? buildCharacterDescriptionDraft(toDraft(currentEntity, type, initialDraft)),
    );
    setAliasesText(nextSnapshot?.aliasesText ?? formatEntityAliasesInput(currentEntity?.aliases ?? []));
    setRemoveImage(false);
    setSaveError(null);
    setSaving(false);
    setStagedImage((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
      }

      return null;
    });
    saveStateChangeRef.current("idle", null);
  }, [createDraftStorageKey, entity?.id, initialDraft, mode, type]);

  useEffect(() => {
    if (mode !== "create" || initialDraft || !createDraftStorageKey) {
      return;
    }

    writeStoredCreateDraft(createDraftStorageKey, {
      aliasesText,
      characterDescriptionText,
      draft: {
        ...draft,
        imageFile: null,
        removeImage: false,
      },
    });
  }, [
    aliasesText,
    characterDescriptionText,
    createDraftStorageKey,
    draft,
    initialDraft,
    mode,
  ]);

  useEffect(() => {
    const handle = window.requestAnimationFrame(() => {
      nameInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(handle);
  }, [entity?.id, mode, type]);

  const raceSpeciesOptions = useMemo(
    () => availableLoreEntries.filter((entry) => entry.type === "race_species"),
    [availableLoreEntries],
  );
  const characterTargets = useMemo(
    () =>
      availableCharacterTargets.filter(
        (target) => target.type === "character" && target.id !== entity?.id,
      ),
    [availableCharacterTargets, entity?.id],
  );

  const raceIds = new Set(raceSpeciesOptions.map((option) => option.id));
  const characterIds = new Set(characterTargets.map((option) => option.id));
  const raceValue = toSelectValue(draft.raceLoreEntryId ?? null, raceIds);
  const speciesValue = toSelectValue(draft.speciesLoreEntryId ?? null, raceIds);
  const holderValue = toSelectValue(draft.currentHolderEntityCardId ?? null, characterIds);
  const currentImageUrl = !removeImage && !stagedImage ? entity?.imageUrl ?? null : null;
  const canSave = draft.name.trim().length > 0 && !saving;
  const typeLabel = t(entityCardTypeLabelKeys[type]);
  const title =
    mode === "create"
      ? t("entities.form.createTitle", { type: typeLabel })
      : draft.name || t("entities.form.untitledTitle", { type: typeLabel });
  const description = t(entityCardTypeDescriptionKeys[type]);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    saveStateChangeRef.current("saving", null);

    try {
      await onSave({
        ...draft,
        aliases: parseEntityAliasesInput(aliasesText),
        imageFile: stagedImage?.file ?? null,
        physicalDescription:
          type === "character" ? characterDescriptionText : draft.physicalDescription,
        personalityTraits: type === "character" ? "" : draft.personalityTraits,
        removeImage,
      });
      if (mode === "create") {
        clearStoredCreateDraft(createDraftStorageKey);
      }
      if (stagedImage) {
        URL.revokeObjectURL(stagedImage.url);
      }
      setSaving(false);
      setStagedImage(null);
      setRemoveImage(false);
      saveStateChangeRef.current("saved", null);
    } catch (error) {
      const message = t(formatEntityErrorMessage(error));
      setSaveError(message);
      setSaving(false);
      saveStateChangeRef.current("error", message);
    }
  }

  function handlePickImage(file: File) {
    if (stagedImage) {
      URL.revokeObjectURL(stagedImage.url);
    }

    setRemoveImage(false);
    setStagedImage({
      file,
      url: URL.createObjectURL(file),
    });
  }

  const statusPill = (
    <div className="inline-flex items-center gap-2 rounded-full bg-[#f3e8da] px-4 py-2 text-sm text-foreground">
      {saving ? (
        <>
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("entities.save.saving")}
        </>
      ) : saveError ? (
        <>
          <TriangleAlert className="h-4 w-4" />
          {t("entities.save.error")}
        </>
      ) : (
        t("entities.save.ready")
      )}
    </div>
  );

  return (
    <section className="project-shell-editor px-8 py-8 sm:px-10 sm:py-10 xl:px-12 xl:py-12">
      <div className="space-y-8">
        <div className="border-b border-[#e7dccd] pb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
                {mode === "create"
                  ? t("entities.form.newEyebrow", { type: typeLabel })
                  : typeLabel}
              </p>
              <h3 className="font-display text-[clamp(2.35rem,4vw,3.9rem)] leading-[0.95] text-foreground">
                {title}
              </h3>
              <p className="max-w-4xl text-[1.1rem] leading-8 text-muted-foreground">
                {description}
              </p>
            </div>
            {mode === "edit" ? statusPill : null}
          </div>

          {saveError ? (
            <p className="mt-5 rounded-[1.25rem] border border-destructive/15 bg-destructive/8 px-4 py-3 text-sm text-destructive">
              {saveError}
            </p>
          ) : null}
        </div>

        <div className="grid gap-x-14 gap-y-10 xl:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)]">
          <div className="space-y-8">
            <EntityCardImageField
              altText={draft.name || typeLabel}
              currentImageUrl={currentImageUrl}
              generateActionLabel={entity?.generateActionLabel ?? null}
              onGenerateImage={mode === "edit" ? onGenerateImage : undefined}
              onImageLoadError={onMediaLoadError}
              onPickImage={handlePickImage}
              onRemoveImage={() => {
                if (stagedImage) {
                  URL.revokeObjectURL(stagedImage.url);
                }
                setRemoveImage(true);
                setStagedImage(null);
              }}
              stagedImageUrl={stagedImage?.url ?? null}
            />

            <EditorialField htmlFor="entity-card-name" label={t("entities.form.fields.name")}>
              <Input
                className={LINE_INPUT_CLASS_NAME}
                id="entity-card-name"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, name: event.target.value }))
                }
                placeholder={getNamePlaceholder(type, t)}
                ref={nameInputRef}
                value={draft.name}
              />
            </EditorialField>

            {type === "character" ? (
              <>
                <EditorialField htmlFor="entity-card-aliases" label={t("entities.form.fields.aliases")}>
                  <textarea
                    className={LINE_TEXTAREA_THIN_CLASS_NAME}
                    id="entity-card-aliases"
                    onChange={(event) => setAliasesText(event.target.value)}
                    placeholder={t("entities.form.placeholders.aliases")}
                    value={aliasesText}
                  />
                </EditorialField>

                <div className="grid gap-8 sm:grid-cols-2">
                  <EditorialTextBlock label={t("entities.form.fields.age")}>
                    <Input
                      className={LINE_INPUT_CLASS_NAME}
                      id="entity-card-age"
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, ageText: event.target.value }))
                      }
                      placeholder={t("entities.form.placeholders.age")}
                      value={draft.ageText}
                    />
                  </EditorialTextBlock>

                  <EditorialField htmlFor="entity-card-race" label={t("entities.form.fields.raceLink")}>
                    <Select
                      onValueChange={(value) =>
                        setDraft((current) => ({
                          ...current,
                          raceLoreEntryId: value === NONE_VALUE ? null : value,
                        }))
                      }
                      value={raceValue}
                    >
                      <SelectTrigger
                        aria-label={t("entities.form.fields.raceLink")}
                        className={LINE_SELECT_TRIGGER_CLASS_NAME}
                        id="entity-card-race"
                      >
                        <SelectValue placeholder={t("entities.form.placeholders.raceLink")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>{t("entities.form.noLinkedLore")}</SelectItem>
                        {draft.raceLoreEntryId && !raceIds.has(draft.raceLoreEntryId) ? (
                          <SelectItem disabled value={raceValue}>
                            {t("entities.form.missingLoreEntry")}
                          </SelectItem>
                        ) : null}
                        {raceSpeciesOptions.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {draft.raceLoreEntryId && !raceIds.has(draft.raceLoreEntryId) ? (
                      <MissingSelectionMessage targetId={draft.raceLoreEntryId} />
                    ) : null}
                  </EditorialField>
                </div>
              </>
            ) : null}

            {type === "item-object" ? (
              <>
                <EditorialField htmlFor="entity-card-category" label={t("entities.form.fields.itemCategory")}>
                  <Select
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        itemCategory: value === NONE_VALUE ? null : (value as EntityItemCategory),
                      }))
                    }
                    value={draft.itemCategory ?? NONE_VALUE}
                  >
                    <SelectTrigger
                      aria-label={t("entities.form.fields.itemCategory")}
                      className={LINE_SELECT_TRIGGER_CLASS_NAME}
                      id="entity-card-category"
                    >
                      <SelectValue placeholder={t("entities.form.placeholders.itemCategory")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>{t("entities.form.noCategory")}</SelectItem>
                      {entityItemCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {t(entityItemCategoryLabelKeys[category])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditorialField>

                <EditorialField htmlFor="entity-card-holder" label={t("entities.form.fields.currentHolder")}>
                  <Select
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        currentHolderEntityCardId: value === NONE_VALUE ? null : value,
                      }))
                    }
                    value={holderValue}
                  >
                    <SelectTrigger
                      aria-label={t("entities.form.fields.currentHolder")}
                      className={LINE_SELECT_TRIGGER_CLASS_NAME}
                      id="entity-card-holder"
                    >
                      <SelectValue placeholder={t("entities.form.placeholders.currentHolder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>{t("entities.form.noCurrentHolder")}</SelectItem>
                      {draft.currentHolderEntityCardId
                      && !characterIds.has(draft.currentHolderEntityCardId) ? (
                        <SelectItem disabled value={holderValue}>
                          {t("entities.form.missingCharacter")}
                        </SelectItem>
                      ) : null}
                      {characterTargets.map((target) => (
                        <SelectItem key={target.id} value={target.id}>
                          {target.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {draft.currentHolderEntityCardId
                  && !characterIds.has(draft.currentHolderEntityCardId) ? (
                    <MissingSelectionMessage targetId={draft.currentHolderEntityCardId} />
                  ) : null}
                </EditorialField>
              </>
            ) : null}

            {type === "machine" ? (
              <EditorialField htmlFor="entity-card-holder" label={t("entities.form.fields.currentHolder")}>
                <Select
                  onValueChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      currentHolderEntityCardId: value === NONE_VALUE ? null : value,
                    }))
                  }
                  value={holderValue}
                >
                  <SelectTrigger
                    aria-label={t("entities.form.fields.currentHolder")}
                    className={LINE_SELECT_TRIGGER_CLASS_NAME}
                    id="entity-card-holder"
                  >
                    <SelectValue placeholder={t("entities.form.placeholders.machineHolder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t("entities.form.noCurrentHolder")}</SelectItem>
                    {draft.currentHolderEntityCardId
                    && !characterIds.has(draft.currentHolderEntityCardId) ? (
                      <SelectItem disabled value={holderValue}>
                        {t("entities.form.missingCharacter")}
                      </SelectItem>
                    ) : null}
                    {characterTargets.map((target) => (
                      <SelectItem key={target.id} value={target.id}>
                        {target.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {draft.currentHolderEntityCardId
                && !characterIds.has(draft.currentHolderEntityCardId) ? (
                  <MissingSelectionMessage targetId={draft.currentHolderEntityCardId} />
                ) : null}
              </EditorialField>
            ) : null}

            {type === "creature" ? (
              <EditorialField htmlFor="entity-card-species" label={t("entities.form.fields.speciesLink")}>
                <Select
                  onValueChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      speciesLoreEntryId: value === NONE_VALUE ? null : value,
                    }))
                  }
                  value={speciesValue}
                >
                  <SelectTrigger
                    aria-label={t("entities.form.fields.speciesLink")}
                    className={LINE_SELECT_TRIGGER_CLASS_NAME}
                    id="entity-card-species"
                  >
                    <SelectValue placeholder={t("entities.form.placeholders.speciesLink")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t("entities.form.noLinkedLore")}</SelectItem>
                    {draft.speciesLoreEntryId && !raceIds.has(draft.speciesLoreEntryId) ? (
                      <SelectItem disabled value={speciesValue}>
                        {t("entities.form.missingLoreEntry")}
                      </SelectItem>
                    ) : null}
                    {raceSpeciesOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {draft.speciesLoreEntryId && !raceIds.has(draft.speciesLoreEntryId) ? (
                  <MissingSelectionMessage targetId={draft.speciesLoreEntryId} />
                ) : null}
              </EditorialField>
            ) : null}
          </div>

          <div className="flex min-h-full flex-col gap-10">
            {type === "character" ? (
              <>
                <EditorialField htmlFor="entity-card-description" label={t("entities.form.fields.description")}>
                  <textarea
                    className={LINE_TEXTAREA_CLASS_NAME}
                    id="entity-card-description"
                    onChange={(event) => setCharacterDescriptionText(event.target.value)}
                    placeholder={t("entities.form.placeholders.description")}
                    value={characterDescriptionText}
                  />
                </EditorialField>

                <EditorialField htmlFor="entity-card-skills" label={t("entities.form.fields.skillsAndAbilities")}>
                  <textarea
                    className={LINE_TEXTAREA_CLASS_NAME}
                    id="entity-card-skills"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        skillsAndAbilities: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.skillsAndAbilities")}
                    value={draft.skillsAndAbilities}
                  />
                </EditorialField>

                <EditorialField
                  className="flex-1"
                  htmlFor="entity-card-notes"
                  label={t("entities.form.fields.backgroundStory")}
                >
                  <textarea
                    className={LINE_TEXTAREA_LARGE_CLASS_NAME}
                    id="entity-card-notes"
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, notes: event.target.value }))
                    }
                    placeholder={t("entities.form.placeholders.backgroundStory")}
                    value={draft.notes}
                  />
                </EditorialField>
              </>
            ) : null}

            {type === "item-object" ? (
              <>
                <EditorialField htmlFor="entity-card-description" label={t("entities.form.fields.itemDescription")}>
                  <textarea
                    className={LINE_TEXTAREA_MEDIUM_CLASS_NAME}
                    id="entity-card-description"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        itemDescription: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.itemDescription")}
                    value={draft.itemDescription}
                  />
                </EditorialField>

                <EditorialField htmlFor="entity-card-origin" label={t("entities.form.fields.origin")}>
                  <textarea
                    className={LINE_TEXTAREA_MEDIUM_CLASS_NAME}
                    id="entity-card-origin"
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, origin: event.target.value }))
                    }
                    placeholder={t("entities.form.placeholders.origin")}
                    value={draft.origin}
                  />
                </EditorialField>
              </>
            ) : null}

            {type === "creature" ? (
              <>
                <EditorialField htmlFor="entity-card-creature-description" label={t("entities.form.fields.creatureDescription")}>
                  <textarea
                    className={LINE_TEXTAREA_CLASS_NAME}
                    id="entity-card-creature-description"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        creatureDescription: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.creatureDescription")}
                    value={draft.creatureDescription}
                  />
                </EditorialField>

                <EditorialField htmlFor="entity-card-creature-abilities" label={t("entities.form.fields.creatureAbilities")}>
                  <textarea
                    className={LINE_TEXTAREA_CLASS_NAME}
                    id="entity-card-creature-abilities"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        creatureAbilities: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.creatureAbilities")}
                    value={draft.creatureAbilities}
                  />
                </EditorialField>
              </>
            ) : null}

            {type === "location" ? (
              <>
                <EditorialField htmlFor="entity-card-location-description" label={t("entities.form.fields.locationDescription")}>
                  <textarea
                    className={LINE_TEXTAREA_MEDIUM_CLASS_NAME}
                    id="entity-card-location-description"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        locationDescription: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.locationDescription")}
                    value={draft.locationDescription}
                  />
                </EditorialField>

                <EditorialField
                  className="flex-1"
                  htmlFor="entity-card-location-notes"
                  label={t("entities.form.fields.locationNotes")}
                >
                  <textarea
                    className={LINE_TEXTAREA_LARGE_CLASS_NAME}
                    id="entity-card-location-notes"
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, locationNotes: event.target.value }))
                    }
                    placeholder={t("entities.form.placeholders.locationNotes")}
                    value={draft.locationNotes}
                  />
                </EditorialField>
              </>
            ) : null}

            {type === "machine" ? (
              <>
                <EditorialField htmlFor="entity-card-machine-description" label={t("entities.form.fields.machineDescription")}>
                  <textarea
                    className={LINE_TEXTAREA_MEDIUM_CLASS_NAME}
                    id="entity-card-machine-description"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        machineDescription: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.machineDescription")}
                    value={draft.machineDescription}
                  />
                </EditorialField>

                <EditorialField htmlFor="entity-card-machine-capabilities" label={t("entities.form.fields.machineCapabilities")}>
                  <textarea
                    className={LINE_TEXTAREA_CLASS_NAME}
                    id="entity-card-machine-capabilities"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        machineCapabilities: event.target.value,
                      }))
                    }
                    placeholder={t("entities.form.placeholders.machineCapabilities")}
                    value={draft.machineCapabilities}
                  />
                </EditorialField>

                <EditorialField htmlFor="entity-card-origin" label={t("entities.form.fields.origin")}>
                  <textarea
                    className={LINE_TEXTAREA_MEDIUM_CLASS_NAME}
                    id="entity-card-origin"
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, origin: event.target.value }))
                    }
                    placeholder={t("entities.form.placeholders.machineOrigin")}
                    value={draft.origin}
                  />
                </EditorialField>
              </>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#e7dccd] pt-6">
          <div className="flex flex-wrap items-center gap-3">
            {mode === "create" && onCancelCreate ? (
              <Button
                className="rounded-[1rem] px-1 py-0 text-base font-medium text-foreground hover:bg-transparent"
                onClick={() => {
                  clearStoredCreateDraft(createDraftStorageKey);
                  onCancelCreate();
                }}
                type="button"
                variant="ghost"
              >
                {t("common.cancel")}
              </Button>
            ) : null}

            {mode === "edit" && onDelete ? (
              <Button
                className="rounded-[1rem] px-1 py-0 text-base font-medium text-foreground hover:bg-transparent"
                onClick={onDelete}
                type="button"
                variant="ghost"
              >
                <Trash2 className="h-4 w-4" />
                {t("entities.dialogs.deleteConfirm")}
              </Button>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <Button
              className="rounded-[1.1rem] px-8 py-4 text-base"
              disabled={!canSave}
              onClick={() => void handleSave()}
              type="button"
            >
              {saving
                ? mode === "create"
                  ? t("entities.form.creatingAction", { type: typeLabel })
                  : t("common.saving")
                : mode === "create"
                  ? t("entities.form.createAction", { type: typeLabel })
                  : t("common.save")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
